import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogMain,
  DialogFooter,
  DialogTitle,
} from '@ncoder/ui';
import { useCreateFeatureRequest } from '@/hooks/feedback/useCreateFeatureRequest';

export interface RequestFeatureFormData { headline: string; description: string; }

const defaultValues: RequestFeatureFormData = { headline: '', description: '' };

interface RequestFeatureModalProps { open: boolean; onClose: () => void; }

export function RequestFeatureModal({ open, onClose }: RequestFeatureModalProps) {
  const { mutate, isPending } = useCreateFeatureRequest();

  const formMethods = useForm<RequestFeatureFormData>({ defaultValues, mode: 'onChange' });
  const { formState: { isValid }, reset } = formMethods;

  useEffect(() => { if (open) reset(defaultValues); }, [open, reset]);

  const handleCancel = () => { reset(defaultValues); onClose(); };

  const onSubmit = (data: RequestFeatureFormData) => {
    mutate(data, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Request a feature</DialogTitle>
          <DialogDescription className="text-xs">
            Tell us what you'd like to see. We use your input to plan improvements.
          </DialogDescription>
        </DialogHeader>

        <FormProvider {...formMethods}>
          <DialogMain className="space-y-3">
            <div className="space-y-1.5">
              <label htmlFor="request-feature-headline" className="text-xs font-medium text-foreground">Headline</label>
              <input
                id="request-feature-headline"
                placeholder="Short title for your feature idea"
                {...formMethods.register('headline', { required: true })}
                disabled={isPending}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="request-feature-description" className="text-xs font-medium text-foreground">Description</label>
              <textarea
                id="request-feature-description"
                placeholder="Describe the feature and how it would help you"
                rows={4}
                {...formMethods.register('description', { required: true })}
                disabled={isPending}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary resize-none disabled:opacity-50"
              />
            </div>
          </DialogMain>
        </FormProvider>

        <DialogFooter>
          <button type="button" onClick={handleCancel} disabled={isPending} className="px-3 py-1.5 text-xs rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={formMethods.handleSubmit(onSubmit)} disabled={!isValid || isPending} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:pointer-events-none">
            {isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            {isPending ? 'Submitting…' : 'Submit request'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
